import { useMemo, useCallback } from 'react';
import create from 'zustand';
import type { Actor } from '@avatar/types';
import {
  ActorVariant,
  ClipActor,
  StudioProjectVersion,
  TtsVersionSelectOption,
} from '../../../../server/graphql/__generated.types';
import { useUserContext } from '../../../context/user/UserContext';
import { getFirstNonArchivedVariant } from '../utils/actor';
import { useStudioToolbar } from '../../../pages/dashboard/studio/StudioToolbarProvider';

export type ActiveActorAndVariant = {
  actor: Actor;
  variant: Actor['variants'][0];
};

type ActiveActorStateAndActions = {
  actor: ActiveActorAndVariant | null;
  setActor: (selectedActor: ActiveActorAndVariant) => void;
};

/**
 * This hook serves as a persisted state for actors
 * @todo it likely makes sense to consolidate this
 * and the useActiveActorHook
 */
const usePersistedActorStore = create<ActiveActorStateAndActions>(set => ({
  actor: null,
  setActor: selectedActor =>
    set(state => ({
      ...state,
      actor: selectedActor,
    })),
}));

export type TTSVersion = {
  label: TtsVersionSelectOption;
  model?: string;
};

export default function useActiveActor(
  actor: Actor,
  projectVersion: StudioProjectVersion
) {
  const { data: userQuery } = useUserContext();

  const { activeActor, setActiveActor, version, setVersion } =
    useStudioToolbar();

  const activeClipActor = useMemo<ClipActor>(() => {
    const {
      // reset __typename for typing purposes
      __typename,
      variants,
      ...currentActor
    } = activeActor.actor;

    return {
      ...currentActor,
      variant: activeActor.variant,
      ttsVersion: version.model,
    };
  }, [activeActor.actor, activeActor.variant, version.model]);

  const updateVersion = useCallback(
    (variant: ActorVariant, variantModelVersion?: string) => {
      if (
        !userQuery?.user.settings.displayTTSVersionSelector ||
        projectVersion !== StudioProjectVersion.Legacy
      ) {
        setVersion({
          model: variant.versions[TtsVersionSelectOption.Latest].model,
          label: TtsVersionSelectOption.Latest,
        });
      } else if (!variantModelVersion) {
        const defaultSetting = userQuery?.user.settings?.defaultTTSVersion;
        const hasVersion = Object.keys(variant.versions).find(
          v => v === defaultSetting
        );

        if (hasVersion) {
          const modelVersion =
            variant.versions[defaultSetting as TtsVersionSelectOption]?.model;
          setVersion({
            model: modelVersion,
            label: defaultSetting as TtsVersionSelectOption,
          });
        }
      } else {
        const label = Object.keys(variant.versions).find(key => {
          const model = variant.versions[key as TtsVersionSelectOption]?.model;
          return (
            (key as TtsVersionSelectOption) &&
            variantModelVersion === model &&
            model !== undefined
          );
        });
        setVersion({
          model: variantModelVersion,
          label: label as TtsVersionSelectOption,
        });
      }
    },
    [
      projectVersion,
      setVersion,
      userQuery?.user.settings?.defaultTTSVersion,
      userQuery?.user.settings.displayTTSVersionSelector,
    ]
  );

  const updateActiveActor = useCallback(
    (
      newActor: Actor,
      newVariant?: ActorVariant,
      newVariantModelVersion?: string
    ) => {
      const variant =
        newVariant ||
        (getFirstNonArchivedVariant(newActor?.variants) ??
          newActor?.variants[0]);

      setActiveActor({
        actor: newActor,
        variant,
      });

      updateVersion(variant, newVariantModelVersion);
    },
    [setActiveActor, updateVersion]
  );

  const setActiveActorVariant = useCallback(
    (newVariant: ActorVariant, newVariantModelVersion?: string) => {
      if (
        activeActor.actor.variants.find(
          ({ id, isArchived }) => id === newVariant.id && !isArchived
        )
      ) {
        setActiveActor({ ...activeActor, variant: newVariant });
      }
      updateVersion(newVariant, newVariantModelVersion);
    },
    [activeActor, setActiveActor, updateVersion]
  );
  const activeActorWithVersion = useMemo(
    () => ({ ...activeActor, version: version?.model || undefined }),
    [activeActor, version]
  );
  return {
    activeActor: activeActorWithVersion,
    activeClipActor,
    activeActorVariantVersion: version.label,
    setActiveActor: updateActiveActor,
    setActiveActorVariant,
  };
}
